import dedent from 'ts-dedent'
import { MILES_TO_M } from '../const'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class GeoBuffer extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.buffer',
        group: 'Spatial Operations',
        title: 'ST Buffer',
        description: `
      This component adds a new column with a buffer of the original input geography.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'distance',
          type: WorkflowComponentParamType.Number,
          title: 'Distance',
          optional: true,
          default: 10
        },
        {
          name: 'units',
          type: WorkflowComponentParamType.Selection,
          title: 'Units',
          options: ['Meters', 'Miles'],
          default: 'Meters'
        }
      ]
    )
  }

  getInnerSql({ params, connection, envVars }: WorkflowComponentCodeParams) {
    let distance = params.distance
    if (params.units === 'Miles') {
      distance = `(${params.distance} * ${MILES_TO_M})`
    }
    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`SELECT ST_BUFFER(${params.geo}, ${distance}) AS ${params.geo}_buffer, *
        FROM ${connection.quoteName(params.source as string)}`
      case Provider.Snowflake:
        return dedent`SELECT ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.ST_BUFFER(${
          params.geo
        }, ${distance}) AS ${params.geo}_buffer, *
            FROM ${connection.quoteName(params.source as string)}`
      case Provider.Postgres:
        return dedent`SELECT
                  ST_TRANSFORM(
                    ST_BUFFER(
                      ST_TRANSFORM(
                        ${params.geo},
                        4326
                      )::geography,
                      ${distance}
                    )::geometry,
                    ST_SRID(${params.geo})
                  ) AS ${params.geo}_buffer,
                  *
                FROM ${connection.quoteName(params.source as string)}`
      case Provider.Redshift:
        return dedent`SELECT
                  ST_TRANSFORM(
                    ST_BUFFER(
                      ST_TRANSFORM(${params.geo}, 3857),
                      ${distance} / COS(
                        (
                          ST_YMIN(${params.geo})
                          + ST_YMAX(${params.geo})
                        ) * PI() / 360.
                      )
                    ),
                    ST_SRID(${params.geo})
                  ) AS ${params.geo}_buffer,
                  *
                FROM ${connection.quoteName(params.source as string)}`
      case Provider.DatabricksRest:
        return dedent`SELECT /*+ REPARTITION(40, ${params.geo}_buffer) */
                  SEDONA_ST_ASBINARY(
                    SEDONA_ST_TRANSFORM(
                      SEDONA_ST_SETSRID(
                        SEDONA_ST_BUFFER(
                          SEDONA_ST_TRANSFORM(${params.geo}_processed, 'epsg:3857'),
                          ${distance} / COS(
                            (
                              SEDONA_ST_YMIN(${params.geo}_processed)
                              + SEDONA_ST_YMAX(${params.geo}_processed)
                            ) * PI() / 360.
                          )
                        )
                      , 3857)
                    , 'epsg:4326'
                    )
                  ) AS ${params.geo}_buffer,
                  * EXCEPT(${params.geo}_processed)
                FROM (
                  SELECT /*+ REPARTITION(40, ${params.geo}_processed) */
                    *, SEDONA_ST_SETSRID(
                    SEDONA_ST_GEOMFROMWKB(${params.geo})
                    , 4326) AS ${params.geo}_processed
                  FROM ${connection.quoteName(params.source as string)}
                )`
      case Provider.Oracle: {
        const geoColumn = params.geo as string
        const bufferColumn = `${params.geo}_buffer`
        return dedent`SELECT SDO_GEOM.SDO_BUFFER(t.${geoColumn}, ${distance}, 0.005, 'unit=M') AS ${bufferColumn}, t.*
        FROM ${connection.quoteName(params.source as string)} t`
      }
      default:
        return null
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Snowflake:
        return '1.0.0'
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.geo}_buffer`,
      type: connection.getGeoDataType()
    })
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { GeoBuffer }
